// Purchase Order Status
export type POStatus = 'pending' | 'approved' | 'ordered' | 'received' | 'closed' | 'cancelled';

// Line Item Status
export type POItemStatus = 'pending' | 'partial' | 'received' | 'cancelled';

// Vendor from vendor list
export interface Vendor {
  code: string;
  name: string;
  tax_id: string;
  phone: string;
  contact: string;
  email: string;
  bank_name: string;
  branch: string;
  bank_code: string;
  account: string;
}

// Line item in a purchase order
export interface POLineItem {
  line_no: number;
  product_name: string;
  specification: string;
  quantity: number;
  unit_price: number;
  amount: number;
  currency: string;
  remarks?: string;
  // Firestore-only fields (per-item tracking)
  expected_delivery?: string;
  actual_delivery?: string;
  status: POItemStatus;
  received_qty: number;
}

// Purchase Order document
export interface PurchaseOrder {
  po_id: string;
  po_number: string;
  purchase_date: string;
  expected_delivery: string;
  delivery_address: string;
  payment_terms?: string;
  vendor_code: string;
  items: POLineItem[];
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  grand_total: number;
  currency: string;
  status: POStatus;
  flow_id?: string;
  sheet_id?: string;
  sheet_url?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  notes?: string;
}

// PO Response with vendor data enriched
export interface POResponse extends PurchaseOrder {
  vendor?: Vendor;
}

// Create PO Request
export interface CreatePORequest {
  vendor_code: string;
  purchase_date: string;
  expected_delivery: string;
  delivery_address: string;
  payment_terms?: string;
  items: Omit<POLineItem, 'line_no' | 'amount' | 'status' | 'received_qty'>[];
  currency?: string;
  notes?: string;
}

// Update PO Request
export interface UpdatePORequest {
  vendor_code?: string;
  purchase_date?: string;
  expected_delivery?: string;
  delivery_address?: string;
  payment_terms?: string;
  items?: Omit<POLineItem, 'line_no' | 'amount'>[];
  currency?: string;
  notes?: string;
}

// Update Status Request
export interface UpdateStatusRequest {
  status: POStatus;
}

// Update Item Status Request
export interface UpdateItemStatusRequest {
  line_no: number;
  status: POItemStatus;
  received_qty?: number;
}

// PO List Response
export interface POListResponse {
  purchase_orders: POResponse[];
  total: number;
  limit: number;
  offset: number;
}

// PO Number Response
export interface PONumberResponse {
  po_number: string;
}

// PO Filter for list queries
export interface POFilter {
  vendor_code?: string;
  status?: POStatus;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

// Status display labels (Chinese)
export const PO_STATUS_LABELS: Record<POStatus, string> = {
  pending: '待審核',
  approved: '已核准',
  ordered: '已下單',
  received: '已收貨',
  closed: '已結案',
  cancelled: '已取消',
};

export const PO_ITEM_STATUS_LABELS: Record<POItemStatus, string> = {
  pending: '待交貨',
  partial: '部分交貨',
  received: '已收貨',
  cancelled: '已取消',
};

// Status badge colors
export const PO_STATUS_COLORS: Record<POStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  ordered: 'bg-purple-100 text-purple-800',
  received: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
};

export const PO_ITEM_STATUS_COLORS: Record<POItemStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  partial: 'bg-orange-100 text-orange-800',
  received: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

// Default tax rate (5%)
export const DEFAULT_TAX_RATE = 0.05;

// Default currency
export const DEFAULT_CURRENCY = 'TWD';

// Payment terms options
export const PAYMENT_TERMS_OPTIONS = [
  { value: '月結30天', label: '月結30天' },
  { value: '月結60天', label: '月結60天' },
  { value: '月結90天', label: '月結90天' },
  { value: '貨到付款', label: '貨到付款' },
  { value: '預付款', label: '預付款' },
  { value: '票期30天', label: '票期30天' },
  { value: '票期60天', label: '票期60天' },
  { value: '票期90天', label: '票期90天' },
];

// Currency options
export const CURRENCY_OPTIONS = [
  { value: 'TWD', label: 'TWD (新台幣)' },
  { value: 'USD', label: 'USD (美元)' },
  { value: 'CNY', label: 'CNY (人民幣)' },
  { value: 'EUR', label: 'EUR (歐元)' },
  { value: 'JPY', label: 'JPY (日圓)' },
];
